import { Button, Popover, Typography } from "@material-ui/core";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { getDateFnsLocale } from "@utils/dateUtils";
import { format } from "date-fns";
import clsx from "clsx";
import _ from "lodash";
import moment from "moment-timezone";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import { t } from "ttag";
import urlFlagMap from "../../constants/urlToFlagMapping";
import { BACNET_LINES } from "../../constants/bacnet";
import { Arrow as SvgArrow, Calendar } from "../../icons/";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { IDateRange } from "../../models/Selections";
import CoolDateRangePicker from "../CoolDateRangePicker/CoolDateRangePicker";
import ExternalsMenu from "../Menu/ExternalsMenu";
import UnitsMenu from "../Menu/UnitsMenu";
import MenuDropDown from "../MenuDropDown/MenuDropDown";
import useStyles from "./SelectionsMenu.style";
import { truncateText } from "@components/TruncatedText";
import BacnetUnitsMenu from "@components/Menu/BacnetUnitsMenu";
import ModbusUnitsMenu from "@components/Menu/ModbusUnitsMenu";
import { IEntityType } from "@models/Units";
export interface ISelectionsMenuProps {
  onCustomerSelect?: (id: string) => void;
  onSiteSelect?: (id: string) => void;
  onSystemSelect?: (id: string, changePage?: boolean) => void;
  onUnitSelect?: (id: string, changePage?: boolean) => void;
  onDateSelect?: (clearFilter: boolean) => void;
  hideCustomerSelection?: boolean;
  hideSiteSelection?: boolean;
  hideSystemSelection?: boolean;
  hideUnitSelection?: boolean;
  showDateRangePicker?: boolean;
  showOneDatePicker?: boolean;
  disableOneDatePicker?: boolean;
  hideAllOptionsFromDropdown?: string[];
  customGeneralNames?: any;
  showSensors?: boolean;
  minDate?: any;
  countControlUnits?: boolean;
  screenTitle?: string;
  hideOutdoor?: boolean;
  hideBsBox?: boolean;
  hideIndoor?: boolean;
  hideOther?: boolean;
  hideIfCustomerNotMulti?: boolean;
  applySiteTypeFiltering?: boolean;
  passSelectedDate?: any;
  hideControl?: boolean;
  noRangeLimit?: boolean;
  allowUnassignedControls?: boolean;
  pageCustomSelector?: any;
  textAllCustomers?: boolean;
  customDateRangePickerComponent?: any;
  showBACnetDevices?: boolean;
  showModbusUnits?: boolean;
  showBacnetDevicesMenu?: boolean;
  showBacnetLinesMenu?: boolean;
  showModbusLinesMenu?: boolean;
  showModbusDevicesMenu?: boolean;
  disableModbusMenus?: boolean;
  showSystemCustomGroups?: boolean;
}

const generalNamesConst = {
  customer: t`All Customers`,
  selectCustomer: t`Select Customer`,
  site: t`All Sites`,
  system: t`All Systems`,
  unit: t`All Units`,
  bacnet: t`All devices`,
  bacnetLine: t`All lines`,
  modbusLine: t`Select line`,
  modbusDevice: t`All Devices`,
};

const SelectionsMenu = ({
  onCustomerSelect,
  onSiteSelect,
  onSystemSelect,
  onUnitSelect,
  onDateSelect,
  hideCustomerSelection = false,
  hideSiteSelection = false,
  hideSystemSelection = false,
  hideUnitSelection = false,
  showDateRangePicker = false,
  showOneDatePicker = false,
  disableOneDatePicker = false,
  hideAllOptionsFromDropdown = [],
  textAllCustomers = false,
  customGeneralNames = {},
  showSensors = false,
  minDate = new Date("1900-01-01"),
  countControlUnits = false,
  screenTitle = "",
  hideOutdoor = false,
  hideBsBox = false,
  hideIndoor = false, // it means hide service
  hideIfCustomerNotMulti = false,
  applySiteTypeFiltering = false,
  passSelectedDate,
  hideControl = false,
  noRangeLimit = false,
  allowUnassignedControls = false,
  pageCustomSelector,
  customDateRangePickerComponent = null,
  showBACnetDevices,
  showModbusUnits,
  showBacnetDevicesMenu = false,
  showBacnetLinesMenu = false,
  showModbusLinesMenu = false,
  showModbusDevicesMenu = false,
  disableModbusMenus = false,
  showSystemCustomGroups = false,
}: ISelectionsMenuProps) => {

  const c = useStyles();
  const theme = useStoreState((s) => s.theme);
  const selections = useStoreState((s) => s.selections.selections);
  const updateSelections = useStoreActions((a) => a.selections.updateSelections);
  const customers = useStoreState((s) => s.selections.getCustomersBySelection);
  const userPref = useStoreState((s) => s.users.userPreferences);
  const updateUserPreferences = useStoreActions(a => a.users.updateUserPreferences)
  const sites = useStoreState((s) => s.selections.getSitesBySelection);
  const systems = useStoreState((s) => s.selections.getSystemsBySelection);
  const getUnitName = useStoreState((s) => s.units.getUnitName);
  const allUnits = useStoreState((s) => s.units.allUnits);
  const countUnitsFor = useStoreState((s) => s.units.countUnitsFor);
  const sitesFlags = useStoreState((s) => s.sites.sitesFlags);
  const getUnit = useStoreState((s) => s.units.getUnit);
  const allDevices = useStoreState((state) => state.devices.allDevices);

  const getPowerMeterName = useStoreState((s) => s.powerMeters.getPowerMeterName);
  const getBacnetDeviceName = useStoreState((s) => s.bacnetDevices.getBacnetDeviceName);
  const getSensorName = useStoreState((s) => s.sensors.getSensorName);
  const getSensorGroupName = useStoreState((s) => s.sensors.getSensorGroupName);
  const hasExternals = useStoreState((s) => s.selections.getExternalsBySelection);
  const hasConnectedBacnetUnits = useStoreState((s) => s.selections.hasConnectedBacnetUnits);
  const hasConnectedModbusUnits = useStoreState((s) => s.selections.hasConnectedModbusUnits);
  const getModbusUnit = useStoreState((s) => s.modbusUnits.getModbusUnit);
  const [anchorEl, setAnchorEl] = React.useState({ unitsMenu: null, dateRangePicker: null, externalsMenu: null, bacnetsMenu: null });

  const unitsMenuOpen = Boolean(anchorEl.unitsMenu);
  const dateRangePickerOpen = Boolean(anchorEl.dateRangePicker);
  const externalsMenuOpen = Boolean(anchorEl.externalsMenu);
  const selectedUnitName = getUnitName(selections.unitId, true);
  const selectedSensorName = selections.sensorId === "all" ? t`All Sensors` : getSensorName(selections.sensorId);
  const selectedSensorGroupName = selections.sensorGroupId === "all" ? t`All Sensor Groups` : getSensorGroupName(selections.sensorGroupId);
  const selectedPowerMeterName = selections.powerId === "all" ? t`All Power Meters` : getPowerMeterName(selections.powerId);
  const selectedBacnetUnitName = selections.bacnetUnitId === "all" ? t`All BACnet devices` : getBacnetDeviceName(selections.bacnetUnitId);
  const selectedModbusUnitName = selections.modbusUnitId === "all" ? t`All Modbus Units` : (getModbusUnit(selections.modbusUnitId)?.name || "");
  const location = useLocation();
  const customersUnitCounts = useStoreState((s) => s.customers.customersUnitCounts);
  const allSites = useStoreState((state) => state.sites.allSites);

  const countUnits = useCallback(
    (itemType: IEntityType, id: string, countControlUnits: boolean) => {
      switch (itemType) {
        case "customer":
          return `(${customersUnitCounts[id]?.total || 0})`;
        case "site":
          return `(${customersUnitCounts[allSites?.[id]?.customer]?.sites?.[id] || 0})`;
        case "system":
          return selections.siteId ? `(${countUnitsFor(itemType, id, countControlUnits)})` : "";
        default:
          return `(${countUnitsFor(itemType, id, countControlUnits)})`;
      }
    },
    [customersUnitCounts, selections?.customerId, selections.siteId, allUnits]
  );

  /**
   * This function sets the selected customer based on a prop saved in the UserPreferences.
   * @returns
   */
  const initSelectedCustomer = () => {
    //If userPref is undefined. return.
    if (!userPref) {
      return
    }

    //If customers list is undefined or empty. return.
    if (!customers || !customers.length) {
      return
    }

    //Get the selectedPropCustomer from the userPref.
    const { selectedProCustomer } = userPref

    //Check if the selectedProCustomer is in the list of customers for this user.
    //If it is - set it.
    //If it is not - set the first customer in the list.
    if (selectedProCustomer) {
      const customer = customers.find(({ id }) => selectedProCustomer === id) ?? customers[0];
      updateSelections({ type: "customer", data: customer.id });
    } else {
      updateSelections({ type: "customer", data: customers[0].id });
    }
  }

  useEffect(() => {
    if (!selections.customerId) {
      initSelectedCustomer()
    }
  }, []);


  useEffect(() => {
    const { systemId = '', unitId = '', siteId = '' } = selections;

    if (siteId) {
      const pageFlag = urlFlagMap[screenTitle];
      if (pageFlag) {
        if (sitesFlags[siteId] && !sitesFlags[siteId][pageFlag]) {
          updateSelections({ type: "site", data: null });
          return;
        }
      }
    }

    if (systemId === "externals" && !showSensors) {
      updateSelections({ type: "system", data: null });
      return;
    }

    if (systemId === "bacnetUnits" && !showBACnetDevices) {
      updateSelections({ type: "system", data: null });
      return;
    }

    if (systemId === "modbusUnits" && !showModbusUnits) {
      updateSelections({ type: "system", data: null });
      return;
    }

    const unit = getUnit(unitId);
    const { isServiceSite } = (selections.siteId && sitesFlags[selections.siteId]) || {};

    if (!unit?.isVisible) {
      updateSelections({ type: "unit", data: null });
      return;
    }

    if (applySiteTypeFiltering && !isServiceSite) {
      if (unit && unit?.type !== 1) {
        updateSelections({ type: "unit", data: null });
      }
      return;
    }

    if (unit && hideOutdoor && unit?.type === 2) {
      updateSelections({ type: "unit", data: null });
      return;
    }

    if (unit && hideBsBox && unit?.type === 4) {
      updateSelections({ type: "unit", data: null });
      return;
    }
    if (unit && hideIndoor && unit?.type === 3) {
      updateSelections({ type: "unit", data: null });
      return;
    }
    if (unit && unit.serviceUnits?.length && unit?.type === 1 && !hideIndoor && isServiceSite) {
      updateSelections({ type: "unit", data: unit.serviceUnits[0] });
      return;
    }
  }, [selections.siteId, location]);

  // Unit in popover selected
  const setUnit = (unitId: string | null, changePage = true) => {
    if (onUnitSelect) {
      onUnitSelect(unitId as any);
      handleClose();
      return;
    }
    updateSelections({ type: "unit", data: unitId });
    handleClose();
  };

  // Selections state management
  const setCustomer = (id: string) => {
    updateSelections({ type: "customer", data: id });
    if (!!id) {
      updateUserPreferences({ selectedProCustomer: id })
    }
    onCustomerSelect && onCustomerSelect(id);
  };

  const setSite = (id: string) => {
    updateSelections({ type: "site", data: id });
    onSiteSelect && onSiteSelect(id);
  };
  const setDevice = (id: string) => {
    updateSelections({ type: "device", data: id });
  };

  const setSystem = (id: string, changePage = true) => {
    updateSelections({ type: "system", data: id });
    onSystemSelect && onSystemSelect(id);
  };

  const handleNewDateRange = (range: IDateRange | null) => {
    // If null - user chose Cancel, so we don't change current selection
    if (range) {
      updateSelections({ type: "time", data: range });
    }
    setAnchorEl({ unitsMenu: null, dateRangePicker: null, externalsMenu: null, bacnetsMenu: null });
  };
  const handleDateChange = (date: any | null) => {
    // If null - user chose Cancel, so we don't change current selection
    onDateSelect && onDateSelect(false);
    if (date) {
      const now = new Date();
      let endDate;
      if (new Date(new Date(date?.getTime()).setHours(23, 59, 59)) > now) {
        endDate = now;
      } else {
        endDate = new Date(new Date(date?.getTime()).setHours(23, 59, 59));
      }
      const range = { startDate: new Date(new Date(date.getTime()).setHours(0, 0, 0)), endDate };
      passSelectedDate && passSelectedDate(range);
      updateSelections({ type: "time", data: range });
    }
    setAnchorEl({ unitsMenu: null, dateRangePicker: null, externalsMenu: null, bacnetsMenu: null });
  };

  const generalNames = { ...generalNamesConst, ...customGeneralNames };

  const getOptions = useCallback((itemType: "customer" | "site" | "system", items: any) => {
    const options = items.reduce((arr: any, item: any) => {
      if (itemType === "system" && (urlFlagMap[screenTitle] && sitesFlags[item?.site] && !sitesFlags[item?.site][urlFlagMap[screenTitle]])
      ) {
        return arr;
      }

      if (itemType === "site" && (urlFlagMap[screenTitle] && sitesFlags[item?.id] && !sitesFlags[item.id][urlFlagMap[screenTitle]])) {
        return arr;
      }

      const itemName = itemType === 'customer' ?
        (userPref?.nicknames && userPref?.nicknames[item.id]) || item.name :
        item.name


      arr.push({
        name: `${truncateText(itemName, 40)} ${countUnits(itemType, item.id, countControlUnits)}`,
        value: item.id,
        key: item.id,
        type: item.type,
        device: item.device || ""
      });
      if (itemType === 'customer') {
        return arr.sort((a: any, b: any) => a.name.localeCompare(b.name));
      }
      return arr;
    }, []);
    if (itemType === "system" && ((showSensors || showSystemCustomGroups) && hasExternals)) { // need to be there only if site is selected or its fine?
      const name = t`External Sources`;
      options.push({
        name,
        value: "externals",
        key: "externals",
        type: "externals",
        device: ""
      });
    }

    if (itemType === "system" && (showBACnetDevices || showSystemCustomGroups) && hasConnectedBacnetUnits) {
      const name = t`BACnet Devices`;
      options.push({
        name,
        value: "bacnetUnits",
        key: "bacnetUnits",
        type: "bacnetUnits",
        device: ""
      });
    }

    if (itemType === "system" && showModbusUnits && hasConnectedModbusUnits) {
      options.push({
        name: t`Modbus Units`,
        value: "modbusUnits",
        key: "modbusUnits",
        type: "modbusUnits",
        device: ""
      });
    }

    return _.isEmpty(options) && itemType !== "site"
      ? [{ name: `No ${itemType}s`, value: "", key: itemType, type: 0 }]
      : options;
  }, [countControlUnits, countUnitsFor, hasExternals, hasConnectedBacnetUnits, hasConnectedModbusUnits, showModbusUnits, screenTitle, showSensors, sitesFlags, userPref, customersUnitCounts, selections.siteId, showSystemCustomGroups]);

  const bacnetDevices = useMemo(() => {
    return Object.values(allDevices || {})
      .filter((entry) => {
        return (entry.type === 3 || entry.itcVersion) && entry.site === selections.siteId;
      })
      .map((unit: any) => ({
        name: unit.name,
        value: unit.id,
        key: unit.id,
      }));
  }, [allDevices, selections.siteId]);

  // Reset protocol selections when the site actually changes (not on initial mount).
  const prevSiteIdRef = useRef(selections.siteId);
  useEffect(() => {
    const prev = prevSiteIdRef.current;
    prevSiteIdRef.current = selections.siteId;
    if (prev !== selections.siteId && selections.siteId) {
      updateSelections({ type: "bacnetDevice", data: null });
      updateSelections({ type: "bacnetLine", data: null });
      updateSelections({ type: "modbusLine", data: null });
      updateSelections({ type: "modbusDevice", data: null });
    }
  }, [selections.siteId]);

  const bacnetLines = useMemo(() => {
    // don't show any line selection if no devices are available
    if(!bacnetDevices || !bacnetDevices.length) {
      return [];
    }

    return BACNET_LINES;
  }, [bacnetDevices]);

  const modbusLines = useMemo(() => {
    const caDevice = selections.modbusDeviceId ? (allDevices || {})[selections.modbusDeviceId] : null;
    const hvacLines: Record<string, any> | undefined = caDevice?.hvacLinesExtended;

    if (hvacLines && Object.keys(hvacLines).length > 0) {
      const rtuLines = Object.entries(hvacLines)
        .filter(([, cfg]) => cfg?.ngCode === "MODBUS")
        .map(([lineNum, cfg]) => {
          const p = cfg.ngProps;
          const label = `L${lineNum}`;
          return { name: label, value: String(lineNum), key: lineNum };
        });
      return [{ name: "IP / TCP", value: "0", key: "0" }, ...rtuLines];
    }

    // Fallback for devices without hvacLinesExtended (old firmware)
    return [
      { name: "IP / TCP", value: "0", key: "0" },
      { name: "L1", value: "1", key: "1" },
      { name: "L2", value: "2", key: "2" },
      { name: "L3", value: "3", key: "3" },
    ];
  }, [selections.modbusDeviceId, allDevices]);

  const modbusCADevices = useMemo(() => {
    if (!selections.siteId) return [];
    return Object.values(allDevices || {})
      .filter((d: any) => d.site === selections.siteId && d.supportsModbus)
      .map((d: any) => ({
        name: d.serial,
        value: d.id,
        key: d.id,
      }));
  }, [allDevices, selections.siteId]);

  const setDatePickerDialogReference = (ref: any) => {
  };
  const printDateRange = (range: IDateRange | null) => {
    if (_.isNil(range)) {
      return "Select date range";
    }
    return `${moment(range.startDate).format("ll")} - ${moment(range.endDate).format("ll")}`;
  };

  // Click handler for dropdown button which assigns correct anchor and opens Popover
  type IAnchor = "unitsMenu" | "dateRangePicker" | "externalsMenu" | "bacnetsMenu";
  const handleClick = useCallback(
    (anchorName: IAnchor) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setAnchorEl({ ...anchorEl, [anchorName]: event.currentTarget });
    },
    []
  );
  const handleClose = () => {
    setAnchorEl({ unitsMenu: null, dateRangePicker: null, externalsMenu: null, bacnetsMenu: null });
  };

  const customersOptions = useMemo(() => {
    if (!hideCustomerSelection) {
      return getOptions("customer", customers);
    }
  }, [hideCustomerSelection, customers, getOptions]);

  const sitesOptions = useMemo(() => {
    if (!hideSiteSelection) {
      return getOptions("site", sites);
    }
  }, [hideSiteSelection, sites, getOptions]);

  const systemsOptions = useMemo(() => {
    if (!hideSystemSelection) {
      return getOptions("system", systems);
    }
  }, [hideSystemSelection, systems, getOptions]);

  useEffect(() => {
    if (sitesOptions?.length === 1 && selections.siteId !== sitesOptions[0]?.value) {
      setSite(sitesOptions[0].value);
    }
  }, [sitesOptions, selections.siteId]);

  return (
    <div className={c.selectionsContainer}>
      {(hideCustomerSelection || (hideIfCustomerNotMulti && customersOptions?.length === 1)) ?
        <></> :
        <MenuDropDown
          onChange={setCustomer}
          value={selections.customerId || ""}
          options={[
            ...(
              customersOptions?.length < 2 ?
                [] :
                [{
                  name: generalNames[textAllCustomers ? "customer" : 'selectCustomer'] + ` (${customersOptions.length})`,
                  value: "",
                  key: "customer",
                  type: 0
                }]
            ),
            ...customersOptions]}
          hideArrow={customersOptions.length === 1}
        />
      }
      {!hideSiteSelection && (
        <MenuDropDown
          onChange={setSite}
          hideArrow={sitesOptions?.length === 1}
          value={selections.siteId || ""}
          disableNotAllowed={true}
          options={[
            ...(
              (hideAllOptionsFromDropdown.includes("site")) ?
                [] :
                [{
                  name: generalNames["site"] + ` (${sitesOptions.length})`,
                  value: "",
                  key: "site",
                  type: 0
                }]
            ),
            ...sitesOptions]}
        />
      )}
      {!hideSystemSelection && (
        <MenuDropDown
          onChange={setSystem}
          setDevice={setDevice}
          value={
            selections.systemId || ""
          }
          options={[
            ...(
              (hideAllOptionsFromDropdown.includes("system")) ?
                [] :
                [{
                  name: systemsOptions.length ? generalNames["system"] : t`No systems`,
                  value: "",
                  key: "system",
                  type: 0
                }]
            ),
            ...systemsOptions
          ]}
        />
      )}
      {(!hideUnitSelection && selections.systemId !== "externals" && selections.systemId !== "bacnetUnits" && selections.systemId !== "modbusUnits") && (
        <React.Fragment>
          <Button
            style={{ width: "20%" }}
            disableRipple
            className={clsx(c.selectUnitButton, { [c.btnDisabled]: false })}
            classes={{
              label: c.selectUnitButton__text,
              endIcon: c.Arrow
            }}
            size="large"
            onClick={handleClick("unitsMenu")}
            endIcon={<SvgArrow />}
            disabled={!selections.siteId}
          >
            <Typography className={c.selectedUnitStyle}>
              {selections.unitId ? selectedUnitName : generalNames.unit}
            </Typography>
          </Button>
          <Popover
            open={unitsMenuOpen}
            anchorEl={anchorEl.unitsMenu}
            onClose={handleClose}
            classes={{ paper: c.paperBg }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: -10,
              horizontal: "left"
            }}
          >
            <UnitsMenu
              hideOutdoor={hideOutdoor}
              hideBsBox={hideBsBox}
              hideIndoor={hideIndoor}
              hideControl={hideControl}
              hideOther={true}
              hideSystemSelection={hideSystemSelection}
              onUnitSelect={setUnit}
              screenTitle={screenTitle}
              generalNaming={generalNames.unit}
              showGeneralOption={hideAllOptionsFromDropdown.indexOf("unit") > -1}
              applySiteTypeFiltering={applySiteTypeFiltering}
              allowUnassignedControls={allowUnassignedControls}
            />
          </Popover>
        </React.Fragment>
      )}
      {(showSensors || showSystemCustomGroups) && selections.systemId === "externals" && (
        <React.Fragment>
          <Button
            style={{ width: "20%" }}
            disableRipple
            className={clsx(c.selectUnitButton)}
            classes={{
              label: c.selectUnitButton__text
            }}
            size="large"
            onClick={handleClick("externalsMenu")}
            endIcon={<SvgArrow />}
          >
            <Typography className={c.selectedUnitStyle}>
              {selections.sensorId ? selectedSensorName : selections.powerId ? selectedPowerMeterName : selections.sensorGroupId ? selectedSensorGroupName : t`Select External Source`}
            </Typography>
          </Button>
          <Popover
            open={externalsMenuOpen}
            anchorEl={anchorEl.externalsMenu}
            onClose={handleClose}
            classes={{ paper: c.paperBg }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: -10,
              horizontal: "left"
            }}
          >
            <ExternalsMenu showSensors={showSensors} handleClose={handleClose} />
          </Popover>
        </React.Fragment>
      )}
      {(showBACnetDevices || showSystemCustomGroups) && selections.systemId == "bacnetUnits" && (
        <React.Fragment>
          <Button
            style={{ width: "20%" }}
            disableRipple
            className={clsx(c.selectUnitButton)}
            classes={{
              label: c.selectUnitButton__text
            }}
            size="large"
            onClick={handleClick("externalsMenu")}
            endIcon={<SvgArrow />}
          >
            <Typography className={c.selectedUnitStyle}>
              {selections.bacnetUnitId ? selectedBacnetUnitName : t`Select BACnet device`}
            </Typography>
          </Button>
          <Popover
            open={externalsMenuOpen}
            anchorEl={anchorEl.externalsMenu}
            onClose={handleClose}
            classes={{ paper: c.paperBg }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: -10,
              horizontal: "left"
            }}
          >
            <BacnetUnitsMenu handleClose={handleClose} />
          </Popover>
        </React.Fragment>
      )}
      {showModbusUnits && selections.systemId == "modbusUnits" && (
        <React.Fragment>
          <Button
            style={{ width: "20%" }}
            disableRipple
            className={clsx(c.selectUnitButton)}
            classes={{
              label: c.selectUnitButton__text
            }}
            size="large"
            onClick={handleClick("externalsMenu")}
            endIcon={<SvgArrow />}
          >
            <Typography className={c.selectedUnitStyle}>
              {selections.modbusUnitId ? selectedModbusUnitName : t`Select Modbus unit`}
            </Typography>
          </Button>
          <Popover
            open={externalsMenuOpen}
            anchorEl={anchorEl.externalsMenu}
            onClose={handleClose}
            classes={{ paper: c.paperBg }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: -10,
              horizontal: "left"
            }}
          >
            <ModbusUnitsMenu handleClose={handleClose} />
          </Popover>
        </React.Fragment>
      )}
      {showBacnetDevicesMenu && selections.siteId && (
        <MenuDropDown
          onChange={(value: any) => {
            updateSelections({ type: "bacnetDevice", data: value});
          }}
          hideArrow={bacnetDevices?.length === 1}
          value={selections.bacnetDeviceId || ""}
          options={
            [
              {
                name: generalNames["bacnet"] + ` (${bacnetDevices.length})`,
                value: "",
                key: "alll",
                type: 0
              },
            ...bacnetDevices
          ]}
          disabled={!selections.siteId}
        />
      )}
      {showBacnetLinesMenu && selections.siteId && (
        <MenuDropDown
          onChange={(value: any) => {
            updateSelections({ type: "bacnetLine", data: value});
          }}
          value={selections.bacnetLineId || ""}
          options={
            [
              {
                name: generalNames["bacnetLine"] + ` (${bacnetLines.length})`,
                value: "",
                key: "all",
              },
            ...bacnetLines
          ]}
          disabled={!selections.siteId}
        />
      )}
      {showModbusDevicesMenu && selections.siteId && (
        <MenuDropDown
          onChange={(value: any) => {
            updateSelections({ type: "modbusDevice", data: value || null });
          }}
          value={selections.modbusDeviceId || ""}
          options={[
            { name: `${generalNames["modbusDevice"]}`, value: "", key: "none" },
            ...modbusCADevices,
          ]}
          disabled={disableModbusMenus || !selections.siteId}
        />
      )}
      {showModbusLinesMenu && selections.siteId && (
        <MenuDropDown
          onChange={(value: any) => {
            updateSelections({ type: "modbusLine", data: value });
          }}
          value={selections.modbusLineId ?? ""}
          options={[
            { name: generalNames["modbusLine"], value: "", key: "none" },
            ...modbusLines,
          ]}
          disabled={disableModbusMenus || !selections.siteId}
        />
      )}
      {showOneDatePicker && (
        <React.Fragment>
          <Button
            disableRipple
            disabled={disableOneDatePicker}
            className={c.selectDateRangeButton}
            classes={{
              label: c.selectDateRangeButton__text,
              iconSizeLarge: c.selectDateRangeButton__icon,
              endIcon: c.Arrow
            }}
            size="large"
            onMouseUp={handleClick("dateRangePicker")}
            endIcon={<SvgArrow />}
            startIcon={<Calendar style={{ marginLeft: "4px" }} color={theme?.palette?.colors?.menuFont} />}
          >
            {`${moment(selections.dateRange?.endDate).format("ll")}`}
          </Button>

          <Popover
            open={dateRangePickerOpen}
            anchorEl={anchorEl.dateRangePicker}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: -10,
              horizontal: "left"
            }}
          >
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getDateFnsLocale()}>
              <DatePicker
                disableToolbar
                variant="static"
                value={selections.dateRange?.endDate}
                onChange={handleDateChange}
                className={c.datePicker}
                autoOk
                maxDate={new Date()}
                minDate={minDate}
                ref={(r) => setDatePickerDialogReference(r)}
                okLabel={t`OK`}
                cancelLabel={t`Cancel`}
                leftArrowButtonProps={{ 'aria-label': t`Previous month` }}
                rightArrowButtonProps={{ 'aria-label': t`Next month` }}
                format={format(new Date(), 'P', { locale: getDateFnsLocale() }).includes('/') ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
              />
            </MuiPickersUtilsProvider>
          </Popover>
        </React.Fragment>
      )}
      {showDateRangePicker &&
        (
          customDateRangePickerComponent === null ?
            <React.Fragment>
              <Button
                disableRipple
                className={c.selectDateRangeButton}
                classes={{
                  label: c.selectDateRangeButton__text,
                  iconSizeLarge: c.selectDateRangeButton__icon,
                  endIcon: c.Arrow
                }}
                size="large"
                onMouseUp={handleClick("dateRangePicker")}
                endIcon={<SvgArrow />}
                startIcon={<Calendar style={{ marginLeft: "4px" }} color={theme?.palette?.colors?.menuFont} />}
              >
                <Typography className={c.selectDateRangeButton__text}>
                  {printDateRange(selections.dateRange)}
                </Typography>
              </Button>

              <Popover
                open={dateRangePickerOpen}
                anchorEl={anchorEl.dateRangePicker}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
                transformOrigin={{
                  vertical: -10,
                  horizontal: "left"
                }}
              >
                <CoolDateRangePicker
                  handleSubmit={handleNewDateRange}
                  initialRange={selections.dateRange}
                  minDate={minDate}
                  noRangeLimit={noRangeLimit}
                />
              </Popover>
            </React.Fragment> :
            customDateRangePickerComponent

        )}
      {pageCustomSelector}
    </div>
  );
};

export default memo(SelectionsMenu);
